import { type ReactNode, useEffect, useState } from 'react';
import { JsonEditor } from '../ui/JsonEditor';
import { checkJsonChanged } from '../../utils/jsonFormat';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { AppButton, AppIconButton } from '../design-system';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export interface OverridesDialogProps {
  open: boolean;
  onClose: () => void;
  layer: 1 | 2 | 3 | 4;
  title: string;
  scopeDescription: string;
  localJson: string;
  savedJson: string;
  exampleJson: string;
  hasAssignment: boolean;
  onSave: (json: string) => void;
  onChange: (val: string) => void;
  isSaving: boolean;
  saveError?: string | null;
  onLoadSaved: () => Promise<string>;
}

export function OverridesDialog({
  open,
  onClose,
  layer,
  title,
  scopeDescription,
  localJson,
  savedJson,
  exampleJson,
  hasAssignment,
  onSave,
  onChange,
  isSaving,
  saveError,
  onLoadSaved,
}: OverridesDialogProps) {
  const [refetching, setRefetching] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [draft, setDraft] = useState(localJson);

  useEffect(() => {
    if (!open) return;
    setDraft(localJson);
    setEditorKey((key) => key + 1);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset from localJson when dialog opens

  const loadContent = (json: string): void => {
    setDraft(json);
    setEditorKey((key) => key + 1);
  };

  let validationError: string | null = null;
  try {
    if (draft.trim() !== '') {
      JSON.parse(draft);
    }
  } catch (err) {
    validationError = err instanceof Error ? err.message : 'Invalid JSON format';
  }

  const isDirty = checkJsonChanged(draft, savedJson);
  const isValid = !validationError;

  const handleRequestClose = (): void => {
    if (layer !== 1 && isDirty && !window.confirm('Discard unsaved JSON changes?')) {
      return;
    }
    onClose();
  };

  const handleSave = (): void => {
    onChange(draft);
    onSave(draft);
  };

  const handleLoadSaved = async (): Promise<void> => {
    if (layer === 4) return;
    setRefetching(true);
    try {
      loadContent(await onLoadSaved());
    } finally {
      setRefetching(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      PaperProps={{
        sx: {
          width: '800px',
          height: '700px',
          minWidth: '500px',
          minHeight: '400px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          resize: 'both',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box component="span" sx={{ typography: 'h6' }}>{title}</Box>
          <Box component="div" sx={{ typography: 'caption', color: 'text.secondary' }}>{scopeDescription}</Box>
        </Box>
        <AppIconButton onClick={handleRequestClose} size="small">
          <CloseIcon />
        </AppIconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      >
        {layer !== 1 && isDirty && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, flexShrink: 0 }}>
            {layer === 4
              ? 'Unsaved changes — click Apply overrides to save'
              : 'Unsaved changes — click Update template to save'}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexShrink: 0 }}>
          <AppButton
            size="small"
            variant="outlined"
            onClick={() => void handleLoadSaved()}
            disabled={layer === 1 || layer === 4 || refetching}
          >
            {layer === 4 ? 'Load saved (N/A)' : refetching ? <CircularProgress size={16} /> : 'Load saved template'}
          </AppButton>
          <AppButton
            size="small"
            variant="outlined"
            onClick={() => loadContent(exampleJson)}
            disabled={
              (layer === 3 && !hasAssignment)
              || !exampleJson
              || exampleJson === '{}'
            }
          >
            Load template example
          </AppButton>
          <AppButton
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={layer === 1 || !isDirty || !isValid || isSaving}
          >
            {isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : layer === 4 ? (
              'Apply overrides'
            ) : (
              'Update template'
            )}
          </AppButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <JsonEditor
            key={editorKey}
            value={draft}
            onChange={setDraft}
            readOnly={layer === 1}
          />
        </Box>

        {isDirty && validationError && (
          <Alert severity="error" sx={{ mt: 2, py: 0.5, flexShrink: 0 }}>
            <strong>JSON Format Error:</strong> {validationError}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mt: 2, py: 0.5, flexShrink: 0 }}>
            <strong>Save Error:</strong> {saveError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, flexShrink: 0 }}>
        <AppButton onClick={handleRequestClose} variant="outlined">
          Cancel
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export interface MergedConfigDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  json: string;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  warnings?: string[];
  /** Optional alerts/chips rendered above the JSON editor (e.g. EFEM reload plan). */
  beforeJson?: ReactNode;
}

export function MergedConfigDialog({
  open,
  onClose,
  title = 'Deploy Preview',
  json,
  isLoading,
  isError,
  error,
  warnings,
  beforeJson,
}: MergedConfigDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '800px',
          height: '700px',
          minWidth: '500px',
          minHeight: '400px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          resize: 'both',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box component="span" sx={{ typography: 'h6' }}>{title}</Box>
        <AppIconButton onClick={onClose} size="small">
          <CloseIcon />
        </AppIconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      >
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error">
            Failed to load merged configuration preview: {error?.message || 'Unknown error'}
          </Alert>
        )}

        {!isLoading && !isError && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 0.5, overflow: 'hidden' }}>
            {warnings?.map((w) => (
              <Alert key={w} severity="warning" sx={{ mb: 1, py: 0.5 }}>
                {w}
              </Alert>
            ))}
            {beforeJson}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <JsonEditor key={json} value={json} readOnly />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, flexShrink: 0 }}>
        <AppButton onClick={onClose} variant="contained">
          Close
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
